/**
 * 하단 상태바. 원작 레이아웃(레벨/이름 → HP/MP → EXP → 스킬 슬롯)을 참고했다.
 * 레이아웃 정의는 docs/game-design.md 11.1 참고.
 */

import Phaser from 'phaser';
import {
  EV,
  type ExpPayload,
  type HpPayload,
  type MesoPayload,
  type MpPayload,
  type StatsChangedPayload,
  type StatusChangedPayload,
  type StatusEffectView,
} from '../game/events';
import type { PlayerState } from '../systems/PlayerState';
import { setFitted } from './fitText';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

interface Bar {
  x: number;
  y: number;
  w: number;
  h: number;
  fill: number;
  light: number;
  ratio: number;
  target: number;
}

const SKILL_KEYS = ['Ctrl', 'Sh', 'A', 'F'];
const LEFT = 20;
const LEFT_W = 172;
const SKILL_BOX = 56;
const SKILL_GAP = 6;

export class StatusBar {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly hpText: Phaser.GameObjects.Text;
  private readonly mpText: Phaser.GameObjects.Text;
  private readonly expText: Phaser.GameObjects.Text;
  private readonly levelText: Phaser.GameObjects.Text;
  private readonly apText: Phaser.GameObjects.Text;
  private readonly nameText: Phaser.GameObjects.Text;
  private readonly mesoText: Phaser.GameObjects.Text;
  private readonly bars: { hp: Bar; mp: Bar; exp: Bar };
  private readonly skillIcons: Phaser.GameObjects.Image[] = [];
  private readonly skillLabels: Phaser.GameObjects.Text[] = [];
  private readonly statusIcons: Phaser.GameObjects.Image[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly state: PlayerState,
  ) {
    const w = scene.scale.width;
    const top = scene.scale.height - 92;
    const skillBlock = SKILL_KEYS.length * SKILL_BOX + (SKILL_KEYS.length - 1) * SKILL_GAP;
    const skillX = w - 18 - skillBlock;
    const barX = LEFT + LEFT_W + 10;
    const barW = Math.max(120, skillX - 14 - barX);

    this.g = scene.add.graphics();

    this.bars = {
      hp: { x: barX, y: top + 16, w: barW, h: 15, fill: COLOR.hp, light: COLOR.hpLight, ratio: 1, target: 1 },
      mp: { x: barX, y: top + 38, w: barW, h: 15, fill: COLOR.mp, light: COLOR.mpLight, ratio: 1, target: 1 },
      exp: { x: barX, y: top + 62, w: barW, h: 12, fill: COLOR.exp, light: COLOR.expLight, ratio: 0, target: 0 },
    };

    const label = (x: number, y: number, size: number, color: string, mono = false) =>
      scene.add.text(x, y, '', {
        fontFamily: mono ? UI_MONO : UI_FONT,
        fontSize: `${size}px`,
        color,
        stroke: '#101018',
        strokeThickness: mono ? 3 : 0,
      });

    this.levelText = label(LEFT, top + 10, 20, TEXT_COLOR.gold);
    this.apText = label(LEFT, top + 32, 11, TEXT_COLOR.warn, true);
    this.nameText = label(LEFT, top + 48, 13, TEXT_COLOR.bright);
    this.mesoText = label(LEFT, top + 66, 12, TEXT_COLOR.dim);
    this.hpText = label(this.bars.hp.x + 8, this.bars.hp.y, 12, TEXT_COLOR.bright, true);
    this.mpText = label(this.bars.mp.x + 8, this.bars.mp.y, 12, TEXT_COLOR.bright, true);
    this.expText = label(this.bars.exp.x + 8, this.bars.exp.y - 1, 11, TEXT_COLOR.bright, true);

    const barSkills = this.barSkills();
    barSkills.forEach((skill, i) => {
      const x = skillX + i * (SKILL_BOX + SKILL_GAP);
      const icon = scene.add.image(x + SKILL_BOX / 2, top + 34, skill?.icon ?? 'icon-attack');
      icon.setOrigin(0.5).setDisplaySize(40, 40);
      if (!skill) icon.setAlpha(0.25);
      this.skillIcons.push(icon);
      const text = scene.add
        .text(x + SKILL_BOX / 2, top + 64, SKILL_KEYS[i] ?? '', {
          fontFamily: UI_MONO,
          fontSize: '11px',
          color: TEXT_COLOR.dim,
        })
        .setOrigin(0.5, 0);
      this.skillLabels.push(text);
    });

    const statusStart = this.bars.hp.x + this.bars.hp.w - 8;
    for (let i = 0; i < 6; i++) {
      const icon = scene.add.image(statusStart - i * 22, top + 24, 'icon-status-stun');
      icon.setOrigin(1, 0.5).setDisplaySize(18, 18).setVisible(false).setDepth(8);
      this.statusIcons.push(icon);
    }

    this.bindEvents();
    this.setName();
    this.state.emitAll();
    this.draw();
  }

  /**
   * PlayerState 는 씬보다 오래 살기 때문에, 씬이 내려갈 때 반드시 구독을 해제해야 한다.
   * (해제하지 않으면 파괴된 Text 를 건드려 예외가 난다 → docs/troubleshooting.md TS-0005)
   */
  destroy(): void {
    const e = this.state.events;
    e.off(EV.hpChanged, this.onHp, this);
    e.off(EV.mpChanged, this.onMp, this);
    e.off(EV.expChanged, this.onExp, this);
    e.off(EV.mesoChanged, this.onMeso, this);
    e.off(EV.statsChanged, this.onStats, this);
    e.off(EV.statusChanged, this.onStatus, this);
    e.off(EV.titleChanged, this.setName, this);
  }

  private bindEvents(): void {
    const e = this.state.events;
    e.on(EV.hpChanged, this.onHp, this);
    e.on(EV.mpChanged, this.onMp, this);
    e.on(EV.expChanged, this.onExp, this);
    e.on(EV.mesoChanged, this.onMeso, this);
    e.on(EV.statsChanged, this.onStats, this);
    e.on(EV.statusChanged, this.onStatus, this);
    e.on(EV.titleChanged, this.setName, this);
  }

  private onHp(p: HpPayload): void {
    this.bars.hp.target = p.maxHp > 0 ? p.hp / p.maxHp : 0;
    setFitted(this.hpText, `${p.hp} / ${p.maxHp}`, this.bars.hp.w - 16, 9);
  }

  private onMp(p: MpPayload): void {
    this.bars.mp.target = p.maxMp > 0 ? p.mp / p.maxMp : 0;
    setFitted(this.mpText, `${p.mp} / ${p.maxMp}`, this.bars.mp.w - 16, 9);
  }

  private onExp(p: ExpPayload): void {
    this.bars.exp.target = Phaser.Math.Clamp(p.ratio, 0, 1);
    const pct = `${(p.ratio * 100).toFixed(0)}%`;
    setFitted(
      this.expText,
      `${p.exp.toLocaleString()} / ${p.need.toLocaleString()}  ${pct}`,
      this.bars.exp.w - 16,
      9,
    );
    setFitted(this.levelText, `Lv.${p.level}`, LEFT_W, 12);
  }

  private onMeso(p: MesoPayload): void {
    setFitted(this.mesoText, `메소 ${p.meso.toLocaleString()}`, LEFT_W, 10);
  }

  private onStats(p: StatsChangedPayload): void {
    if (p.unspentAp > 0) {
      setFitted(
        this.apText,
        `AP ${p.unspentAp}${p.unspentSp > 0 ? ` SP ${p.unspentSp}` : ''}`,
        LEFT_W,
        9,
      );
      this.apText.setVisible(true);
    } else if (p.unspentSp > 0) {
      setFitted(this.apText, `SP ${p.unspentSp}`, LEFT_W, 9);
      this.apText.setVisible(true);
    } else {
      this.apText.setText('');
      this.apText.setVisible(false);
    }
  }

  private onStatus(p: StatusChangedPayload): void {
    this.renderStatuses(p.effects);
  }

  private renderStatuses(effects: StatusEffectView[]): void {
    for (let i = 0; i < this.statusIcons.length; i++) {
      const icon = this.statusIcons[i];
      const fx = effects[i];
      if (!fx) {
        icon.setVisible(false);
        continue;
      }
      icon.setTexture(fx.icon);
      icon.setVisible(true);
      icon.setAlpha(fx.remainMs < 400 ? 0.55 : 1);
    }
  }

  update(delta: number): void {
    const speed = Math.min(1, delta / 120);
    let dirty = false;
    for (const bar of [this.bars.hp, this.bars.mp, this.bars.exp]) {
      if (Math.abs(bar.ratio - bar.target) > 0.0005) {
        bar.ratio += (bar.target - bar.ratio) * speed;
        dirty = true;
      }
    }
    this.updateSkillState();
    if (dirty) this.draw();
  }

  private barSkills() {
    return [this.state.basicAttack(), this.state.quickSkill(0), this.state.quickSkill(1), this.state.quickSkill(2)];
  }

  private updateSkillState(): void {
    const skills = this.barSkills();
    skills.forEach((skill, i) => {
      const icon = this.skillIcons[i];
      const label = this.skillLabels[i];
      if (!skill) {
        icon.setTexture('icon-attack').setAlpha(0.22);
        label.setText(SKILL_KEYS[i] ?? '');
        return;
      }
      icon.setTexture(skill.icon).setDisplaySize(40, 40);
      const usable = skill.mpCost === 0 || this.state.mp >= skill.mpCost;
      icon.setAlpha(usable ? 1 : 0.4);
      const key = SKILL_KEYS[i] ?? '';
      setFitted(label, skill.mpCost > 0 ? `${key} ${skill.mpCost}` : key, SKILL_BOX, 8);
      label.setColor(usable ? TEXT_COLOR.dim : '#7a4a4a');
    });
  }

  private draw(): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    const top = h - 92;
    const g = this.g;
    g.clear();

    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(8, top - 6, w - 16, 92, 10);
    g.fillStyle(COLOR.panel, 0.98);
    g.fillRoundedRect(12, top - 2, w - 24, 84, 8);
    g.lineStyle(2, COLOR.panelLight, 0.8);
    g.strokeRoundedRect(12, top - 2, w - 24, 84, 8);

    // 좌측 정보 블록 구분선
    g.lineStyle(1, COLOR.panelLight, 0.6);
    g.lineBetween(LEFT + LEFT_W, top + 6, LEFT + LEFT_W, top + 74);

    this.drawBar(this.bars.hp);
    this.drawBar(this.bars.mp);
    this.drawBar(this.bars.exp);

    const skills = this.barSkills();
    const skillX = w - 18 - (SKILL_KEYS.length * SKILL_BOX + (SKILL_KEYS.length - 1) * SKILL_GAP);
    skills.forEach((_, i) => {
      const x = skillX + i * (SKILL_BOX + SKILL_GAP);
      g.fillStyle(COLOR.track, 0.9);
      g.fillRoundedRect(x, top + 8, SKILL_BOX, 52, 6);
      g.lineStyle(1, COLOR.panelLight, 0.9);
      g.strokeRoundedRect(x, top + 8, SKILL_BOX, 52, 6);
    });
  }

  private drawBar(bar: Bar): void {
    const g = this.g;
    g.fillStyle(COLOR.track, 1);
    g.fillRoundedRect(bar.x - 2, bar.y - 2, bar.w + 4, bar.h + 4, 4);
    const filled = Math.max(0, bar.w * bar.ratio);
    if (filled > 2) {
      g.fillStyle(bar.fill, 1);
      g.fillRoundedRect(bar.x, bar.y, filled, bar.h, 3);
      g.fillStyle(bar.light, 0.55);
      g.fillRoundedRect(bar.x, bar.y, filled, Math.max(2, bar.h * 0.35), 3);
    }
  }

  get jobLabel(): string {
    return `${this.state.title.name}  ${this.state.name} (${this.state.job.name})`;
  }

  setName(): void {
    setFitted(this.nameText, this.jobLabel, LEFT_W, 10);
  }
}
